import { memo, useState, type ComponentPropsWithoutRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";
import { MermaidDiagram } from "./mermaid-diagram";
import { Check, Copy } from "lucide-react";

type MentionLookup = Map<string, string>;

type MarkdownMessageProps = {
  content: string;
  className?: string;
  /** Map of bare file paths → canonical paths for making inline-code file
   *  references clickable. Built from conversation mention data. */
  mentionPaths?: MentionLookup;
  onClickFilePath?: (path: string) => void;
};

/**
 * Renders a chat message as rich markdown with support for:
 * - GFM tables, strikethrough, task lists
 * - Fenced code blocks with copy button
 * - Mermaid diagrams (```mermaid code blocks)
 * - Inline code, links, images, blockquotes, lists
 */
export const MarkdownMessage = memo(function MarkdownMessage({
  content,
  className,
  mentionPaths,
  onClickFilePath,
}: MarkdownMessageProps) {
  return (
    <div className={cn("markdown-message min-w-0 wrap-break-word", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          pre({ children, ...props }) {
            return (
              <pre {...props} className="not-prose">
                {children}
              </pre>
            );
          },
          code({ className: codeClassName, children, ...props }) {
            const match = /language-(\w+)/.exec(codeClassName ?? "");
            const lang = match?.[1];
            const codeString = String(children).replace(/\n$/, "");

            // Detect if this is an inline code or block code.
            // Block code is wrapped in <pre> by react-markdown.
            const isInline = !codeClassName;

            if (isInline) {
              // If the inline code matches a known file path, render it as
              // a clickable link so the user can jump to the file.
              const trimmed = codeString.trim();
              const resolvedPath = mentionPaths?.get(trimmed);
              if (resolvedPath && onClickFilePath) {
                return (
                  <button
                    type="button"
                    className="inline rounded bg-muted px-1 py-0.5 text-[0.85em] font-mono text-primary underline underline-offset-2 hover:text-primary/80 cursor-pointer"
                    title={resolvedPath}
                    onClick={() => onClickFilePath(resolvedPath)}
                  >
                    {children}
                  </button>
                );
              }
              return (
                <code
                  className="rounded bg-muted px-1 py-0.5 text-[0.85em] font-mono"
                  {...props}
                >
                  {children}
                </code>
              );
            }

            if (lang === "mermaid") {
              return <MermaidDiagram chart={codeString} />;
            }

            return <CodeBlock language={lang} code={codeString} />;
          },
          table({ children, ...props }) {
            return (
              <div className="my-2 overflow-x-auto rounded-md border">
                <table
                  className="min-w-full text-xs"
                  {...(props as ComponentPropsWithoutRef<"table">)}
                >
                  {children}
                </table>
              </div>
            );
          },
          thead({ children, ...props }) {
            return (
              <thead
                className="bg-muted/50"
                {...(props as ComponentPropsWithoutRef<"thead">)}
              >
                {children}
              </thead>
            );
          },
          th({ children, ...props }) {
            return (
              <th
                className="px-2 py-1.5 text-left font-medium"
                {...(props as ComponentPropsWithoutRef<"th">)}
              >
                {children}
              </th>
            );
          },
          td({ children, ...props }) {
            return (
              <td
                className="border-t px-2 py-1.5"
                {...(props as ComponentPropsWithoutRef<"td">)}
              >
                {children}
              </td>
            );
          },
          a({ children, href, ...props }) {
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2 hover:text-primary/80"
                {...(props as ComponentPropsWithoutRef<"a">)}
              >
                {children}
              </a>
            );
          },
          blockquote({ children, ...props }) {
            return (
              <blockquote
                className="my-2 border-l-2 border-muted-foreground/30 pl-3 text-muted-foreground italic"
                {...(props as ComponentPropsWithoutRef<"blockquote">)}
              >
                {children}
              </blockquote>
            );
          },
          ul({ children, ...props }) {
            return (
              <ul
                className="my-1 ml-4 list-disc space-y-0.5 [&_ul]:my-0"
                {...(props as ComponentPropsWithoutRef<"ul">)}
              >
                {children}
              </ul>
            );
          },
          ol({ children, ...props }) {
            return (
              <ol
                className="my-1 ml-4 list-decimal space-y-0.5 [&_ol]:my-0"
                {...(props as ComponentPropsWithoutRef<"ol">)}
              >
                {children}
              </ol>
            );
          },
          li({ children, ...props }) {
            return (
              <li
                className="leading-relaxed"
                {...(props as ComponentPropsWithoutRef<"li">)}
              >
                {children}
              </li>
            );
          },
          h1({ children, ...props }) {
            return (
              <h1
                className="mt-3 mb-1 text-base font-bold"
                {...(props as ComponentPropsWithoutRef<"h1">)}
              >
                {children}
              </h1>
            );
          },
          h2({ children, ...props }) {
            return (
              <h2
                className="mt-3 mb-1 text-sm font-bold"
                {...(props as ComponentPropsWithoutRef<"h2">)}
              >
                {children}
              </h2>
            );
          },
          h3({ children, ...props }) {
            return (
              <h3
                className="mt-2 mb-1 text-sm font-semibold"
                {...(props as ComponentPropsWithoutRef<"h3">)}
              >
                {children}
              </h3>
            );
          },
          hr() {
            return <hr className="my-3 border-muted-foreground/20" />;
          },
          p({ children, ...props }) {
            return (
              <p
                className="my-1 leading-relaxed first:mt-0 last:mb-0"
                {...(props as ComponentPropsWithoutRef<"p">)}
              >
                {children}
              </p>
            );
          },
          img({ src, alt, ...props }) {
            return (
              <img
                src={src}
                alt={alt ?? ""}
                className="my-2 max-w-full rounded-md"
                loading="lazy"
                {...(props as ComponentPropsWithoutRef<"img">)}
              />
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
});

function CodeBlock({ language, code }: { language?: string; code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="group relative my-2 rounded-md border bg-muted/40">
      <div className="flex items-center justify-between border-b px-3 py-1">
        <span className="text-[0.7rem] text-muted-foreground">
          {language ?? "text"}
        </span>
        <button
          type="button"
          onClick={() => void handleCopy()}
          className="flex items-center gap-1 text-[0.7rem] text-muted-foreground opacity-100 transition-opacity hover:text-foreground md:opacity-0 md:group-hover:opacity-100"
          aria-label="Copy code"
        >
          {copied ? (
            <>
              <Check className="size-3" />
              Copied
            </>
          ) : (
            <>
              <Copy className="size-3" />
              Copy
            </>
          )}
        </button>
      </div>
      <pre className="overflow-x-auto p-3">
        <code className="text-xs font-mono leading-relaxed">{code}</code>
      </pre>
    </div>
  );
}
